import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, CloseButton, Divider, Drawer, Group, Indicator, Stack, Text, Title } from '@mantine/core';
import { IconMessage } from '@tabler/icons-react';

import { Milestone, MilestonePathContext, MilestoneStatus } from '../../types/milestones';
import { InteractiveMilestoneStatusBadge } from './InteractiveMilestoneStatusBadge';
import { MilestoneDateLabels } from './MilestoneDateLabels';
import {
  MilestoneNotesScope,
  MilestoneNotesThread,
  notesCountLabel,
} from './MilestoneNotesThread';

type MilestoneDetailDrawerProps = {
  opened: boolean;
  onClose: () => void;
  milestone: Milestone;
  path: MilestonePathContext;
  milestoneLabel?: string;
  scope?: MilestoneNotesScope;
  subawardeeLabel?: string | null;
  onStatusChange: (status: MilestoneStatus) => void;
};

/** Non-interactive notes count cue; the milestone card opens details. */
export function MilestoneNotesIndicator({ notesCount }: { notesCount: number }) {
  if (notesCount <= 0) {
    return null;
  }

  return (
    <Indicator
      label={notesCount > 9 ? '9+' : String(notesCount)}
      size={16}
      offset={2}
      color="gray"
      aria-label={notesCountLabel(notesCount)}
      styles={(theme) => ({
        indicator: {
          fontSize: 9,
          fontWeight: 600,
          padding: '0 3px',
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[6],
          color: theme.white,
        },
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          color: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6],
          pointerEvents: 'none',
        })}
      >
        <IconMessage size={14} stroke={1.5} aria-hidden />
      </Box>
    </Indicator>
  );
}

/** @deprecated Use MilestoneNotesIndicator — card click opens details. */
export function MilestoneNotesTrigger({
  notesCount,
}: {
  notesCount: number;
  onClick?: () => void;
}) {
  return <MilestoneNotesIndicator notesCount={notesCount} />;
}

/** Scalable milestone workspace: summary + notes (extensible for future sections). */
export function MilestoneDetailDrawer({
  opened,
  onClose,
  milestone,
  path,
  milestoneLabel,
  scope,
  subawardeeLabel,
  onStatusChange,
}: MilestoneDetailDrawerProps) {
  const [notesCount, setNotesCount] = useState(milestone.notes_count ?? 0);
  const handleNotesCountChange = useCallback((count: number) => {
    setNotesCount(count);
  }, []);

  const stablePath = useMemo(
    () => ({
      objective_id: path.objective_id,
      key_activity_id: path.key_activity_id,
      goal_id: path.goal_id,
      activity_id: path.activity_id,
      milestone_id: path.milestone_id,
    }),
    [
      path.objective_id,
      path.key_activity_id,
      path.goal_id,
      path.activity_id,
      path.milestone_id,
    ]
  );

  const stableScope = useMemo(
    () => ({
      subawardeeForScope: scope?.subawardeeForScope,
      componentProjectId: scope?.componentProjectId,
    }),
    [scope?.subawardeeForScope, scope?.componentProjectId]
  );

  useEffect(() => {
    setNotesCount(milestone.notes_count ?? 0);
  }, [milestone.id, milestone.notes_count]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      withCloseButton={false}
      title={
        <Stack spacing={4} sx={{ width: '100%' }}>
          <Group position="apart" noWrap align="center">
            <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
              Milestone details
            </Text>
            <CloseButton onClick={onClose} aria-label="Close" />
          </Group>
          <Title order={4} sx={{ lineHeight: 1.3 }}>
            {milestone.title}
          </Title>
          {subawardeeLabel ? (
            <Text size="sm" color="dimmed" weight={500}>
              {subawardeeLabel}
            </Text>
          ) : null}
        </Stack>
      }
      styles={{
        title: {
          width: '100%',
        },
        body: {
          paddingBottom: 24,
        },
      }}
    >
      <Stack spacing="lg">
        <Stack spacing="sm">
          {milestoneLabel ? (
            <Group spacing="xs">
              <Badge size="sm" color="gray" variant="filled">
                {milestoneLabel}
              </Badge>
            </Group>
          ) : null}

          <Group position="apart" align="flex-start" spacing="md" noWrap>
            <MilestoneDateLabels milestone={milestone} />
            <InteractiveMilestoneStatusBadge
              status={milestone.status}
              statusUpdatedAt={
                milestone.status_updated_at ??
                (milestone as { statusUpdatedAt?: string }).statusUpdatedAt
              }
              onStatusChange={onStatusChange}
            />
          </Group>
        </Stack>

        <Divider />

        <Stack spacing="sm">
          <Group position="apart">
            <Text weight={600} size="sm">
              Notes
            </Text>
            {notesCount > 0 ? (
              <Text size="xs" color="dimmed">
                {notesCountLabel(notesCount)}
              </Text>
            ) : null}
          </Group>
          <MilestoneNotesThread
            milestoneId={milestone.id}
            path={stablePath}
            scope={stableScope}
            initialNotesCount={milestone.notes_count ?? 0}
            enabled={opened}
            onNotesCountChange={handleNotesCountChange}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
}
